import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { FileCardLocal } from "@msuite/ui/file-upload";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useMemo } from "react";
import { useWarehouseZone } from "@/hooks/use-warehouse-zone";
import { useWarehouseZoneLabel } from "@/hooks/use-warehouse-zone-label";
import type { WarehouseZonePanelBaseProps } from "../shared";

/**
 * The label that goes on the hall, shown as the PDF that comes out of the printer.
 *
 * **Printing it is not optional.** A transport leg is moved by scanning the load carrier's label
 * and then the hall's, and that second scan is the only thing in the yard that reports where the
 * person holding the box is standing — so a zone with no label on the wall is a zone no leg can be
 * moved in or out of.
 *
 * The preview is the document itself rather than a re-drawn approximation, which is the point:
 * what is checked before the paper is cut — that the code scans, that the name is the one on the
 * door — has to be checked on the thing being printed. A card that laid the same fields out in
 * HTML would agree with the label right up to the first time the template moved.
 *
 * Nothing here reprints or replaces anything. The label is rendered per request from the zone row,
 * so a renamed hall means a new preview on the next load and there is no filed copy to go stale —
 * see `Stock.Printable.generateWarehouseZoneLabel`. The card therefore offers the preview and the
 * download and nothing else: there is no row to unlink and no stored object to delete.
 */
export function WarehouseZoneLabel({ warehouseZoneId }: WarehouseZonePanelBaseProps) {
	/** Context */
	const { t } = useTranslation();

	/** Query */
	const { warehouseZone } = useWarehouseZone(warehouseZoneId);
	const { label, isError } = useWarehouseZoneLabel(warehouseZoneId);

	/** Computed */
	// The card takes a File where the route answers a blob, and the name given here is the one the
	// download lands under — so it carries the zone's number rather than its id.
	const file = useMemo(() => {
		if (!label) return null;
		const number = warehouseZone?.number ?? warehouseZoneId;
		return new File([label], `lagerbereich-${number}.pdf`, { type: "application/pdf" });
	}, [label, warehouseZone?.number, warehouseZoneId]);

	/** Render */
	return (
		<ContentCard
			title={t("stock.warehouse_zone_label")}
			icon={icon("QrCode")}
			variant="plain"
		>
			{isError ? (
				<BlankSlate
					label={t("stock.warehouse_zone_label_failed")}
					Icon={icon("QrCode")}
				/>
			) : (
				<FileCardLocal
					file={file}
					variant="content-card"
					withDownload
					withRemove={false}
					width={null}
					height={null}
				/>
			)}
		</ContentCard>
	);
}
